import type { LucideIcon } from 'lucide-react'

import {
  FIELD_FORMAT,
  formatFieldValue,
} from '@brickup/shared/constants/field-format'
import { FIELD_KEYS, type FieldKey } from '@brickup/shared/constants/field-key'
import { displayUnitSymbol } from '@brickup/shared/constants/units'
import { FIELD_LABELS } from '@brickup/shared/locales/de'
import { FIELDS } from '@/constants/fields'

export type ResolvedField = {
  key: FieldKey
  icon: LucideIcon
  label: string
  short: string
  /** The symbol, ready to render — `m²`, not the `SQUARE_METER` it is stored as. */
  unit?: string
  decimalPlaces?: number
  digitGroupSeparator?: string
  /**
   * The value written out the way this field is written out: its own digits,
   * its own unit. The only spelling a read-only surface may render — a bare
   * a bare `formatNumber` falls back to Intl's three decimals, which is how one
   * column came to hold both `150 m²` and `226,67 m²`.
   */
  format: (value: number) => string
}

/**
 * Single source of truth for the icon, long/short label, and number-formatting
 * options of any entity field (Property, Project, Address, etc.). Use this
 * everywhere a field is rendered with an icon or a label — never hand-pick
 * icons or invent a label for a field listed in `constants/fields.ts`.
 *
 * Resolved once at module load, so every call returns the same object. That is
 * what lets callers drop the result straight into a dependency array.
 */
const RESOLVED = Object.fromEntries(
  FIELD_KEYS.map(key => {
    const { label, short }: { label: string; short?: string } =
      FIELD_LABELS[key]
    const { unit } = FIELD_FORMAT[key] ?? {}
    const symbol = unit ? displayUnitSymbol(unit) : undefined
    // Only the fields a surface renders compactly carry a short form. Where
    // none exists the long label is the answer, never `undefined` on screen.
    return [
      key,
      {
        key,
        ...FIELDS[key],
        ...FIELD_FORMAT[key],
        label,
        short: short ?? label,
        unit: symbol,
        // Delegated rather than rebuilt: written out here it dropped
        // `digitGroupSeparator`, so a year read through the hook printed
        // `1.995` on one customer screen and `1995` on the next.
        format: (value: number) => formatFieldValue(key, value) ?? '',
      },
    ]
  }),
) as Record<FieldKey, ResolvedField>

/**
 * The same field, for a caller that reads several at once.
 *
 * The registry is a table resolved at module load, so this is a lookup rather
 * than state — `useField` is the name a component uses, and this one is what a
 * loop over several fields calls without tripping the rules of hooks.
 */
export const fieldOf = (key: FieldKey): ResolvedField => RESOLVED[key]

export function useField(key: FieldKey): ResolvedField {
  return fieldOf(key)
}
